import React, { useState, useEffect } from 'react';
import { X, Phone, Loader2, CheckCircle } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WHATSAPP_NUMBER = '919179992213';

export default function CallbackPopup() {
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ name: '', phone: '' });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    // Check if popup was shown in this browsing session (last 1 hour)
    const lastShown = localStorage.getItem('bh_callback_ts');
    if (lastShown) {
      const elapsed = Date.now() - parseInt(lastShown, 10);
      // Don't show again within 1 hour
      if (elapsed < 3600000) return;
    }

    const timer = setTimeout(() => {
      setShow(true);
      localStorage.setItem('bh_callback_ts', Date.now().toString());
    }, 30000);

    return () => clearTimeout(timer);
  }, []);

  const handleClose = () => {
    setShow(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Store inquiry in database
      await axios.post(`${API}/service-inquiry`, {
        name: form.name,
        phone: form.phone,
        service_type: 'callback',
        destination: '',
        travel_date: '',
        message: 'Requested a callback from website popup',
      });

      setSuccess(true);

      // Send WhatsApp notification to business owner with client details
      const whatsappMsg = `New Callback Request!\n\nName: ${form.name}\nPhone: ${form.phone}\n\nClient requested a callback from the Beyond Horizons website. Please reach out to them.`;
      const whatsappUrl = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(whatsappMsg)}`;
      window.open(whatsappUrl, '_blank');

      setTimeout(() => { setShow(false); }, 4000);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  if (!show) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" data-testid="callback-popup-overlay">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={handleClose} />

      {/* Modal */}
      <div data-testid="callback-popup" className="relative w-full max-w-md bg-white rounded-3xl shadow-2xl overflow-hidden animate-fade-up">
        {/* Close */}
        <button onClick={handleClose} data-testid="callback-close"
          className="absolute top-4 right-4 z-10 w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition-colors">
          <X className="w-4 h-4 text-gray-500" />
        </button>

        {/* Gold accent bar */}
        <div className="h-1.5 bg-gradient-to-r from-gold-dark via-gold to-gold-light" />

        <div className="p-8">
          {success ? (
            <div className="text-center py-4" data-testid="callback-success">
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h3 className="font-heading text-xl font-bold text-navy mb-2">We'll Call You Back!</h3>
              <p className="font-body text-sm text-gray-500">Our travel expert will reach out to you shortly.</p>
            </div>
          ) : (
            <>
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-gold/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Phone className="w-8 h-8 text-gold" />
                </div>
                <h3 className="font-heading text-2xl font-bold text-navy mb-2">Get a Callback</h3>
                <p className="font-body text-sm text-gray-500">Leave your details and our travel expert will call you within 30 minutes!</p>
              </div>

              <form onSubmit={handleSubmit} data-testid="callback-form" className="space-y-4">
                <div>
                  <input type="text" required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                    data-testid="callback-name" placeholder="Your Name"
                    className="w-full px-4 py-3.5 rounded-2xl border border-gray-200 font-body text-sm focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold transition-colors" />
                </div>
                <div>
                  <input type="tel" required value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })}
                    data-testid="callback-phone" placeholder="Phone Number"
                    className="w-full px-4 py-3.5 rounded-2xl border border-gray-200 font-body text-sm focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold transition-colors" />
                </div>
                <button type="submit" disabled={loading} data-testid="callback-submit"
                  className="w-full flex items-center justify-center gap-2 bg-gold text-white hover:bg-gold-dark rounded-full py-3.5 font-body font-semibold transition-all hover:-translate-y-0.5 hover:shadow-[0_4px_14px_0_rgba(197,160,89,0.39)] disabled:opacity-50">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Phone className="w-4 h-4" /> Request Callback</>}
                </button>
              </form>

              <p className="text-center font-body text-xs text-gray-400 mt-4">
                Or call us directly at <a href="tel:+919179992213" className="text-gold hover:underline">+91 9179992213</a>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
